import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  streamActivity,
  listAuditEntries,
  listAgents,
  type AuditEntry,
  type Agent,
} from "../api/client.js";

const LEVEL_STYLES: Record<string, { bg: string; border: string; dot: string; text: string }> = {
  info: { bg: "bg-blue-50", border: "border-blue-200", dot: "bg-blue-400", text: "text-blue-700" },
  warn: { bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-400", text: "text-amber-700" },
  error: { bg: "bg-red-50", border: "border-red-200", dot: "bg-red-400", text: "text-red-700" },
  critical: { bg: "bg-red-50", border: "border-red-300", dot: "bg-red-600", text: "text-red-800" },
};

const CATEGORY_COLORS: Record<string, string> = {
  tool: "bg-purple-100 text-purple-700",
  auth: "bg-sky-100 text-sky-700",
  workflow: "bg-indigo-100 text-indigo-700",
  sentinel: "bg-orange-100 text-orange-700",
  system: "bg-ink-100 text-ink-600",
};

export function ActivityPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<AuditEntry[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentFilter, setAgentFilter] = useState<string>("");
  const [streaming, setStreaming] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const maxEvents = 200;

  // Load agents for filter dropdown
  useEffect(() => {
    listAgents()
      .then((res) => setAgents(res.agents))
      .catch(() => {});
  }, []);

  // Load initial snapshot then connect SSE
  useEffect(() => {
    let cleanup: (() => void) | null = null;

    // Load snapshot first
    listAuditEntries(50)
      .then((res) => {
        setEvents(res.entries);
      })
      .catch(() => {});

    // Connect to SSE stream
    const connect = () => {
      cleanup = streamActivity(
        (entry) => {
          setStreaming(true);
          setConnectionError(false);
          setEvents((prev) => {
            // Deduplicate by ID
            if (prev.some((e) => e.id === entry.id)) return prev;
            const next = [entry, ...prev];
            return next.length > maxEvents ? next.slice(0, maxEvents) : next;
          });
        },
        () => {
          setStreaming(false);
          setConnectionError(true);
          // Reconnect after 3 seconds
          setTimeout(() => {
            cleanup = null;
            connect();
          }, 3000);
        },
        agentFilter || undefined,
      );
      setStreaming(true);
    };

    connect();

    return () => {
      if (cleanup) cleanup();
    };
  }, [agentFilter]);

  // Count active agents (agents with events in last 60s)
  const now = Date.now();
  const recentAgentIds = new Set(
    events
      .filter((e) => e.agentId && now - new Date(e.timestamp).getTime() < 60_000)
      .map((e) => e.agentId!),
  );

  // Count events by level
  const levelCounts = { info: 0, warn: 0, error: 0, critical: 0 };
  for (const e of events) {
    if (e.level in levelCounts) levelCounts[e.level as keyof typeof levelCounts]++;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 border-b border-ink-100 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-sm font-semibold text-ink-900">Agent Activity</h1>
            <p className="mt-0.5 text-xs text-ink-500">Real-time monitoring of all agent actions</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Agent filter */}
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-700"
            >
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>

            {/* Connection status */}
            <div className="flex items-center gap-1.5">
              <span
                className={`h-2 w-2 rounded-full ${
                  connectionError
                    ? "bg-red-500"
                    : streaming
                      ? "animate-pulse bg-emerald-500"
                      : "bg-ink-300"
                }`}
              />
              <span className="text-2xs text-ink-400">
                {connectionError ? "Reconnecting..." : streaming ? "Live" : "Connecting..."}
              </span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-3 flex items-center gap-4">
          <StatBadge label="Active Agents" value={recentAgentIds.size} color="emerald" />
          <StatBadge label="Events" value={events.length} color="blue" />
          {levelCounts.warn > 0 && (
            <StatBadge label="Warnings" value={levelCounts.warn} color="amber" />
          )}
          {levelCounts.error + levelCounts.critical > 0 && (
            <StatBadge label="Errors" value={levelCounts.error + levelCounts.critical} color="red" />
          )}
        </div>
      </header>

      {/* Event feed */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-4">
          {events.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <p className="text-sm text-ink-400">No activity yet</p>
                <p className="mt-1 text-xs text-ink-300">Events will appear here as agents take actions</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((entry) => (
                <ActivityCard
                  key={entry.id}
                  entry={entry}
                  agentName={agents.find((a) => a.id === entry.agentId)?.name}
                  onNavigate={(path) => navigate(path)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex h-9 shrink-0 items-center justify-between border-t border-ink-100 bg-white px-6">
        <div className="flex items-center gap-2 text-2xs text-ink-400">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              streaming ? "animate-pulse bg-emerald-500" : "bg-ink-300"
            }`}
          />
          {streaming ? "Streaming live events" : "Waiting for connection..."}
        </div>
        <span className="text-2xs text-ink-400">
          {events.length} event{events.length !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );
}

function StatBadge({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: "emerald" | "blue" | "amber" | "red";
}) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 ${colors[color]}`}>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
      <span className="text-2xs">{label}</span>
    </div>
  );
}

function ActivityCard({
  entry,
  agentName,
  onNavigate,
}: {
  entry: AuditEntry;
  agentName?: string;
  onNavigate: (path: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const style = LEVEL_STYLES[entry.level] ?? LEVEL_STYLES.info;
  const catColor = CATEGORY_COLORS[entry.category] ?? CATEGORY_COLORS.system;

  const timeStr = new Date(entry.timestamp).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return (
    <div
      className={`rounded-lg border ${style.border} ${style.bg} transition-all`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
      >
        {/* Status dot */}
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${style.dot}`} />

        {/* Timestamp */}
        <span className="w-20 shrink-0 font-mono text-2xs tabular-nums text-ink-400">
          {timeStr}
        </span>

        {/* Category */}
        <span className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-2xs ${catColor}`}>
          {entry.category}
        </span>

        {/* Action */}
        <span className="flex-1 truncate text-xs text-ink-700">{entry.action}</span>

        {/* Agent name */}
        {agentName && (
          <span className="shrink-0 rounded bg-ink-100 px-1.5 py-0.5 text-2xs text-ink-500">
            {agentName}
          </span>
        )}

        {/* Level badge */}
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-2xs font-medium ${style.text} ${style.bg}`}
        >
          {entry.level}
        </span>

        {/* Expand indicator */}
        {entry.details && (
          <svg
            className={`h-3 w-3 shrink-0 text-ink-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 6l4 4 4-4" />
          </svg>
        )}
      </button>

      {expanded && (
        <div className="border-t border-ink-100 px-4 pb-3 pt-2">
          {entry.details && (
            <p className="text-xs text-ink-600">{entry.details}</p>
          )}
          <div className="mt-2 flex items-center gap-4 text-2xs text-ink-400">
            {entry.agentId && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(`/agents/${entry.agentId}`);
                }}
                className="font-mono hover:text-accent-600 hover:underline"
              >
                Agent: {agentName || entry.agentId.slice(0, 8)}
              </button>
            )}
            {entry.userId && (
              <span className="font-mono">User: {entry.userId.slice(0, 8)}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
